import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Stack,
  Link,
  Chip,
} from '@mui/material';
import {
  Gavel,
  Security,
  Business,
  Email,
  Phone,
  Language,
  GitHub,
  Copyright,
} from '@mui/icons-material';
const LicensingPage = () => {
  const theme = useTheme();

  const sections = [
    {
      id: 'overview',
      title: 'Project Overview',
      icon: <Security />,
      content: `PWGen-Rust is a highly advanced secrets management system developed by Kief Studio LLC. Written in Rust, it provides robust security features, including AES-256-GCM encryption, zero-knowledge architecture, and cross-platform support. This document outlines the ownership, licensing, and usage terms for PWGen-Rust.`
    },
    {
      id: 'ownership',
      title: 'Ownership',
      icon: <Copyright />,
      content: `Kief Studio LLC retains full ownership and intellectual property rights to PWGen-Rust. All code, documentation, and associated assets are the exclusive property of Kief Studio LLC.`
    },
    {
      id: 'licensing',
      title: 'Licensing',
      icon: <Gavel />,
      content: `PWGen-Rust is primarily licensed under the Apache License, Version 2.0. This open-source license allows users to use, modify, distribute, and sublicense the software, including creating forks, provided they comply with the license terms, including:

• Including the original copyright notice (e.g., "Copyright © 2025 Kief Studio LLC") and a copy of the Apache 2.0 License in all copies or substantial portions of the software.

• Disclaiming warranties and limiting liability as outlined in the license.

The full text of the Apache 2.0 License is available in the project repository at https://github.com/KiefStudioMA/PWGen-Rust and on the official website at https://pwgenrust.dev.`
    },
    {
      id: 'commercial',
      title: 'Commercial and Network Use Licensing',
      icon: <Business />,
      content: `For use within an organization, the Apache 2.0 License permits a maximum of five individual users.

Any use of PWGen-Rust on a network, or by more than five individual users within an organization, requires a separate commercial licensing agreement with Kief Studio LLC. This commercial agreement supersedes the Apache 2.0 License for such deployments and will outline specific terms regarding user limits, support, and other provisions. The Apache 2.0 License does not cover network deployments.`
    },
    {
      id: 'forking',
      title: 'Forking and Branding',
      icon: <GitHub />,
      content: `Forks of PWGen-Rust created under the Apache 2.0 License are independent projects and do not represent Kief Studio LLC's official work. Forked projects must not imply endorsement by or affiliation with Kief Studio LLC unless explicitly authorized in writing. Forks are encouraged to use distinct names and branding to avoid confusion with Kief Studio LLC's PWGen-Rust.`
    },
    {
      id: 'contributor',
      title: 'Contributor Policy',
      icon: <GitHub />,
      content: `All contributions to PWGen-Rust, including those from Seron Security (DBA TRaViS) employees or affiliates, must be licensed under the Apache 2.0 License. Contributors agree to grant Kief Studio LLC the necessary rights to incorporate their contributions into the project. Contribution guidelines are outlined in the repository's CONTRIBUTING.md file.`
    },
    {
      id: 'brand',
      title: 'Brand',
      icon: <Copyright />,
      content: `The name "PWGen-Rust" brand is property of Kief Studio LLC. Unauthorized use of the name, logo, or related branding is prohibited, except as permitted under the Apache 2.0 License or with written consent from Kief Studio LLC. Forks must use distinct names to avoid confusion with Kief Studio LLC's PWGen-Rust.`
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of Liability',
      icon: <Gavel />,
      content: `PWGen-Rust is provided "as is" without warranties of any kind, express or implied. Kief Studio LLC shall not be liable for any damages arising from the use of the software, as detailed in the Apache 2.0 License.`
    },
    {
      id: 'governing',
      title: 'Governing Law',
      icon: <Gavel />,
      content: `This document and the rights outlined herein are governed by the laws of Massachusetts, without regard to its conflict of law principles.`
    }
  ];

  const contactInfo = [
    {
      label: 'Company',
      value: 'Kief Studio LLC',
      icon: <Business />
    },
    {
      label: 'Address',
      value: '1061 571 Boston Turnpike, Ste 3\nShrewsbury, MA 01545',
      icon: <Business />
    },
    {
      label: 'Phone',
      value: '+1 617 418 4700',
      icon: <Phone />
    },
    {
      label: 'Email',
      value: 'info@kief.studio',
      icon: <Email />
    },
    {
      label: 'Website',
      value: 'https://kief.studio',
      icon: <Language />,
      isLink: true
    },
    {
      label: 'GitHub',
      value: 'https://github.com/KiefStudioMA/PWGen-Rust',
      icon: <GitHub />,
      isLink: true
    }
  ];

  return (
      
      <Box sx={{ py: { xs: 8, md: 12 }, minHeight: '100vh' }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              PWGen-Rust Licensing Agreement
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
            >
              Ownership, Licensing, and Usage Terms
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<Copyright />}
                label="© 2025 Kief Studio LLC"
                color="primary"
                variant="filled"
              />
              <Chip
                icon={<Gavel />}
                label="Apache 2.0 License"
                color="secondary"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* Main Content */}
          <Stack spacing={4}>
            {sections.map((section, index) => (
              <Paper
                key={section.id}
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ color: 'primary.main', mr: 2 }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {section.title}
                  </Typography>
                </Box>
                
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    whiteSpace: 'pre-line',
                    color: 'text.secondary',
                  }}
                >
                  {section.content}
                </Typography>
              </Paper>
            ))}
          </Stack>

          <Divider sx={{ my: 6 }} />

          {/* Contact Information */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              background: theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(25, 118, 210, 0.05)',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
              Contact Information
            </Typography>
            
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              {contactInfo.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    {item.isLink ? (
                      <Link
                        href={item.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                      >
                        {item.value}
                      </Link>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {item.value}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Footer Note */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Kief Studio LLC reserves the right to update this document as needed to reflect changes in ownership, licensing, or acknowledgments.
            </Typography>
          </Box>
        </Container>
      </Box>
  );
};

export default LicensingPage;
